// @flow
import type { ReceiveActions } from '~/core/state/actions/types';

import {
    ADD_EVENT,
    ADD_SCHEDULE_DATA,
    ATTEMPT_SAVE_SCHEDULE,
    CANCEL_SAVE_SCHEDULE,
    CHANGE_MODE,
    COMPLETE_SAVE_SCHEDULE,
    COPY_DAY_EVENTS,
    DELETE_EVENT,
    DESELECT_SCHEDULE_DAY,
    HIDE_POPUP,
    OVERRIDE_SCHEDULE,
    RESET_SCHEDULE,
    SELECT_DAY,
    SELECT_EVENT,
    SETUP_ACTION_FORM_SCHEDULE,
    SHOW_POPUP,
    UNDO_ALL,
    UPDATE_EVENT_START,
    UPDATE_EVENT_VALUE,
    UPDATE_DAILY_START_TIMES
} from './constants';
import {
    actionFormComponentId,
    actionFormDataId,
    getDataIdFromComponent,
    getScheduleType
} from '~/core/utils/schedule';
import { bulbModes, deviceModes } from '~/core/constants/devices/';
import { RECEIVE_RECIPES, UPDATE_DATA as UPDATE_RECIPES } from '~/core/state/recipes/constants';
import { EDIT_ACTION, END_ATTEMPT_CREATE_ACTION, RECEIVE_ACTIONS } from '~/core/state/actions/constants';
import { scheduleModesEnum, scheduleType } from '~/core/constants/schedule';

import { filter, flow, flowRight, reduce } from 'lodash/fp';
import { fromJS } from 'immutable';
import { handleActions } from 'redux-actions';
import { listDays } from '~/core/constants/days';
import { RECEIVE_PRODUCTS } from '~/core/state/products/constants';
import uuidv4 from 'uuid/v4';
import handleReduce from '~/core/state/handle-reduce';
import { pick } from '~/core/utils/immutable';

const MIN_EVENTS_PER_DAY = 1;
const MINIMUM_EVENT_DURATION = 15;
const MINUTES_IN_DAY = 1440;

const colourTuneableProps = ['colourMode', 'hue', 'saturation', 'status', 'value'];
const tuneableProps = ['brightness', 'colourMode', 'colourTemperature', 'status'];
const warmWhiteProps = ['brightness', 'status'];

const allowedPropsBasedOnType = new Map([
    [scheduleType.COLOUR_TUNEABLE_LIGHT, colourTuneableProps],
    [scheduleType.TUNEABLE_LIGHT, tuneableProps],
    [scheduleType.WARM_WHITE_LIGHT, warmWhiteProps]
]);

const valueMapper = type => value => {
    // For tuneable lights we need to set the colourMode to WHITE
    // so that if a colour light is added to the group, we still show the
    // tuneable portion of the picker when editing an event
    // if no colourMode is present on an event, the picker defaults to the colour portion
    if (type === scheduleType.TUNEABLE_LIGHT) {
        return value.set('colourMode', bulbModes.white);
    }

    return value;
};

const filterEventValues = event => {
    if (event.getIn(['value', 'status']) === deviceModes.OFF) {
        return event;
    }

    let type = event.get('type');
    type =
        type === scheduleType.COLOUR_TUNEABLE_LIGHT && event.getIn(['value', 'colourMode']) === bulbModes.white
            ? scheduleType.TUNEABLE_LIGHT
            : type;

    const value = event.get('value');
    const allowedProps = allowedPropsBasedOnType.get(type);

    if (!allowedProps) {
        return event;
    }

    const mapValue = flowRight(valueMapper(type), pick(allowedProps));
    const nextValues = allowedProps ? mapValue(value) : value;

    if (type === 'warmwhitelight') {
        event.set('colourMode', bulbModes.white);
    }

    return event.set('value', nextValues);
};

const updateEventStartTime = (daySchedule, newStartTime, eventIndex) => {
    return daySchedule.map((event, i) => {
        const timeStart = new Map([
            [i === eventIndex, newStartTime],
            [i < eventIndex, Math.min(event.get('start'), newStartTime - (eventIndex - i) * MINIMUM_EVENT_DURATION)],
            [i > eventIndex, Math.max(event.get('start'), newStartTime + (i - eventIndex) * MINIMUM_EVENT_DURATION)]
        ]).get(true);
        return event.set('start', timeStart);
    });
};

/**
 * Get the schedule from the component if it exists otherwise get it from the data which the component is linked to.
 * A component only contains a copy of a schedule from the data object if this one has been modified and those
 * modifications are not saved nor undone
 * @param  {Map} 	state       The state of the schedule section
 * @param  {string} componentId The id of the schedule component
 * @return {Map} 	A schedule object
 */
const getSchedule = (state, componentId) => {
    const dataId = getDataIdFromComponent(componentId);
    // $FlowFixMe
    return state.getIn(['components', componentId, 'schedule'], state.getIn(['data', dataId, 'schedule']));
};

// $FlowFixMe
const prepareSchedule = schedule => schedule.map(day => day.map(event => event.set('guid', uuidv4())));

const updateRecipesSchedule = (state, { error, payload: { recipes } }) =>
    error
        ? state
        : recipes.reduce((newState, { id, schedule }) => {
              if (schedule) {
                  schedule = prepareSchedule(fromJS(schedule));
              }

              return newState.setIn(
                  ['data', id],
                  fromJS({
                      schedule,
                      scheduleType: scheduleType.RECIPE
                  })
              );
          }, state);

const updateAction = handleReduce((state, { payload: action }) => {
    const scheduleEvent = action.events.find(event => event.type === 'schedule');
    return scheduleEvent
        ? // $FlowFixMe
          state.setIn(
              ['data', action.id, 'schedule'],
              flowRight(prepareSchedule, fromJS)(scheduleEvent.settings.schedule)
          )
        : state;
});

const initialState = fromJS({
    components: {},
    data: {}
});

const actionHandlers = {
    [ADD_EVENT]: (state, { payload: { componentId, defaultEventValue, eventToSplit, maxEvents } }) => {
        let schedule = getSchedule(state, componentId);
        // $FlowFixMe
        const selectedDay = state.getIn(['components', componentId, 'selectedDay']);
        let daySchedule = schedule.get(listDays[selectedDay]);
        // check the number of events in the selected day
        if (daySchedule.size >= maxEvents) {
            return state;
        }

        const nextEventIndex = eventToSplit.get('dayIndex') === selectedDay ? eventToSplit.get('eventIndex') + 1 : 0;
        const nextEventTime = daySchedule.getIn([nextEventIndex, 'start'], MINUTES_IN_DAY);
        // check that the event can be split
        if (
            nextEventIndex === 0
                ? nextEventTime < MINIMUM_EVENT_DURATION
                : nextEventTime - eventToSplit.get('start') < 2 * MINIMUM_EVENT_DURATION
        ) {
            return state;
        }
        const splitTime = nextEventTime - MINIMUM_EVENT_DURATION;

        daySchedule = daySchedule.insert(
            nextEventIndex,
            fromJS({
                guid: uuidv4(),
                start: splitTime,
                value: defaultEventValue ? fromJS(defaultEventValue) : eventToSplit.get('value')
            })
        );
        schedule = schedule.set(listDays[selectedDay], daySchedule);
        // $FlowFixMe
        return state.setIn(['components', componentId, 'schedule'], schedule);
    },

    [ADD_SCHEDULE_DATA]: (state, { payload: { dataId, schedule, scheduleType } }) => {
        // add a guid to each event for animation purpose
        schedule = prepareSchedule(schedule);
        // $FlowFixMe
        return state.setIn(['data', dataId], fromJS({ schedule, scheduleType }));
    },

    [ATTEMPT_SAVE_SCHEDULE]: (state, { payload: { componentId } }) =>
        // $FlowFixMe
        state.setIn(['components', componentId, 'isSavingSchedule'], true),

    [CANCEL_SAVE_SCHEDULE]: (state, { payload: { componentId } }) =>
        // $FlowFixMe
        state.setIn(['components', componentId, 'isSavingSchedule'], false),

    [CHANGE_MODE]: (state, { payload: { componentId, scheduleMode } }) => {
        const path = ['components', componentId, 'scheduleMode'];
        // $FlowFixMe
        const current = state.getIn(path);
        // $FlowFixMe
        return state.withMutations(s => {
            const newMode = scheduleMode === current ? scheduleModesEnum.DEFAULT : scheduleMode;
            s = s.setIn(path, newMode).setIn(['components', componentId, 'selectedEvent'], null);
            s =
                newMode === scheduleModesEnum.COPY_DAY
                    ? s.setIn(['components', componentId, 'selectedDay'], null)
                    : s.setIn(['components', componentId, 'copyDay'], null);
            return s;
        });
    },

    [COMPLETE_SAVE_SCHEDULE]: (state, { payload: { componentId } }) => {
        const dataId = getDataIdFromComponent(componentId);
        // $FlowFixMe
        const currentDataSchedule = state.getIn(['data', dataId, 'schedule']);
        // $FlowFixMe
        return state.withMutations(map =>
            map
                .setIn(
                    ['data', dataId, 'schedule'],
                    // $FlowFixMe
                    state.getIn(['components', componentId, 'schedule']) || currentDataSchedule
                )
                .deleteIn(['components', componentId, 'schedule'])
                .setIn(['components', componentId, 'isSavingSchedule'], false)
                .setIn(['components', componentId, 'copyDay'], null)
                .setIn(['components', componentId, 'scheduleMode'], scheduleModesEnum.DEFAULT)
                .setIn(['components', componentId, 'showingPopup'], false)
                .setIn(['components', componentId, 'selectedEvent'], null)
                .setIn(['components', componentId, 'selectedDay'], null)
        );
    },

    [COPY_DAY_EVENTS]: (state, { payload: { componentId, dayIndex } }) => {
        const path = ['components', componentId, 'copyDay'];
        // $FlowFixMe
        const copyDay = state.getIn(path, null);
        if (copyDay === null) {
            // $FlowFixMe
            return state.setIn(path, fromJS({ dayToCopy: dayIndex, copyToList: [] }));
        } else if (copyDay.get('dayToCopy') === dayIndex) {
            // deselect the day to be copied
            // $FlowFixMe
            return state.deleteIn(path);
        } else if (!copyDay.get('copyToList').includes(dayIndex)) {
            const schedule = getSchedule(state, componentId);
            // get the events from the day to copy and apply a new guid
            const copyDaySchedule = schedule
                .get(listDays[copyDay.get('dayToCopy')])
                .map(event => event.set('guid', uuidv4()));
            // get the events of the day being overwritten so we can store and undo if neccessary
            const dayCopyingToSchedule = schedule.get(listDays[dayIndex]);
            // $FlowFixMe
            return state.withMutations(s =>
                s
                    .setIn(['components', componentId, 'schedule'], schedule.set(listDays[dayIndex], copyDaySchedule))
                    .setIn(path, copyDay.set('copyToList', copyDay.get('copyToList').push(dayIndex)))
                    .setIn([...path, 'copyToListOriginalEvents', listDays[dayIndex]], dayCopyingToSchedule)
            );
        } else if (copyDay.get('copyToList').includes(dayIndex)) {
            // $FlowFixMe
            const originalDayEvents = state.getIn([...path, 'copyToListOriginalEvents', listDays[dayIndex]]);
            // $FlowFixMe
            const indexInList = state.getIn([...path, 'copyToList']).indexOf(dayIndex);
            // $FlowFixMe
            return state.withMutations(s =>
                s
                    .setIn(['components', componentId, 'schedule', listDays[dayIndex]], originalDayEvents)
                    .deleteIn([...path, 'copyToList', indexInList])
                    .deleteIn([...path, 'copyToListOriginalEvents', listDays[dayIndex]])
            );
        }
        return state;
    },

    [DELETE_EVENT]: (state, { payload: { componentId, eventToDelete } }) => {
        let schedule = getSchedule(state, componentId);
        // $FlowFixMe
        const selectedDay = state.getIn(['components', componentId, 'selectedDay']);
        // Do not allow to delete the event that started the day before
        if (eventToDelete.get('dayIndex') !== selectedDay) {
            return state;
        }
        let daySchedule = schedule.get(listDays[selectedDay]);
        // check the number of events in the selected day
        if (daySchedule.size <= MIN_EVENTS_PER_DAY) {
            return state;
        }
        daySchedule = daySchedule.delete(eventToDelete.get('eventIndex'));
        schedule = schedule.set(listDays[selectedDay], daySchedule);
        // $FlowFixMe
        return state.setIn(['components', componentId, 'schedule'], schedule);
    },

    [DESELECT_SCHEDULE_DAY]: (state, { payload: { componentId } }) =>
        // $FlowFixMe
        state
            .setIn(['components', componentId, 'showingPopup'], false)
            .setIn(['components', componentId, 'selectedDay'], null),

    [HIDE_POPUP]: (state, { payload: { componentId } }) =>
        // $FlowFixMe
        state.setIn(['components', componentId, 'showingPopup'], false),

    [RECEIVE_PRODUCTS]: (state, { error, payload }) => {
        if (error) {
            return state;
        }

        return flow(
            filter(product => product.state.schedule),
            reduce((newState, product) => {
                const schedule = prepareSchedule(fromJS(product.state.schedule));
                return newState.setIn(
                    ['data', product.id],
                    fromJS({
                        schedule,
                        scheduleType: getScheduleType(product)
                    })
                );
            }, state)
        )(payload.products);
    },

    [RECEIVE_RECIPES]: updateRecipesSchedule,

    [OVERRIDE_SCHEDULE]: (state, { payload: { componentId, schedule, scheduleType } }) => {
        const dataId = getDataIdFromComponent(componentId);
        // $FlowFixMe
        return state.withMutations(map =>
            map
                .setIn(['data', dataId, 'schedule'], prepareSchedule(schedule))
                .setIn(['data', dataId, 'scheduleType'], scheduleType)
                .deleteIn(['components', componentId, 'schedule'])
        );
    },

    [RESET_SCHEDULE]: (state, { payload: { componentId, defaultSchedule } }) =>
        // $FlowFixMe
        state.mergeIn(['components', componentId], {
            copyDay: null,
            // $FlowFixMe
            schedule: fromJS(defaultSchedule).map(day => day.map(event => event.set('guid', uuidv4()))),
            selectedEvent: null,
            selectedDay: null,
            scheduleMode: scheduleModesEnum.DEFAULT,
            showingPopup: false
        }),

    [SELECT_DAY]: (state, { payload: { componentId, dayIndex } }) => {
        const path = ['components', componentId, 'selectedDay'];
        // $FlowFixMe
        const currentDayIndex = state.getIn(path);
        const isColapsing = currentDayIndex != null;
        if (isColapsing) {
            // $FlowFixMe
            state = state.setIn(['components', componentId, 'selectedEvent'], null);
        }
        // $FlowFixMe
        return state.setIn(path, isColapsing ? null : dayIndex);
    },

    [SELECT_EVENT]: (state, { payload: { componentId, event } }) =>
        // $FlowFixMe
        state.setIn(['components', componentId, 'selectedEvent'], event),

    [SHOW_POPUP]: (state, { payload: { componentId } }) =>
        // $FlowFixMe
        state.setIn(['components', componentId, 'showingPopup'], true),

    [UNDO_ALL]: (state, { payload: { componentId } }) =>
        // $FlowFixMe
        state
            .mergeIn(['components', componentId], {
                copyDay: null,
                scheduleMode: scheduleModesEnum.DEFAULT,
                selectedEvent: null,
                selectedDay: null,
                showingPopup: false
            })
            .deleteIn(['components', componentId, 'schedule']),

    [UPDATE_EVENT_START]: (state, { payload: { componentId, dayIndex, eventIndex, newStartTime } }) => {
        let schedule = getSchedule(state, componentId);

        let daySchedule = schedule.get(listDays[dayIndex]);
        daySchedule = updateEventStartTime(daySchedule, newStartTime, eventIndex);

        schedule = schedule.set(listDays[dayIndex], daySchedule);

        // $FlowFixMe
        return state.withMutations(s =>
            s
                .setIn(['components', componentId, 'schedule'], schedule)
                .setIn(['components', componentId, 'selectedEvent', 'start'], newStartTime)
        );
    },
    [UPDATE_DAILY_START_TIMES]: (state, { payload: { componentId, timeDifference } }) => {
        const oldSchedule = getSchedule(state, componentId);
        const days = listDays.slice(1);

        const newSchedule = days.reduce((schedule: any, day) => {
            const oldDaySchedule = schedule.get(day);
            const newStartTime = Math.max(oldDaySchedule.first().get('start') + timeDifference, 0);
            const newDaySchedule = updateEventStartTime(oldDaySchedule, newStartTime, 0);

            return schedule.set(day, newDaySchedule);
        }, oldSchedule);

        // $FlowFixMe
        return state.withMutations(s => s.setIn(['components', componentId, 'schedule'], newSchedule));
    },
    [UPDATE_EVENT_VALUE]: (state, { payload: { componentId, updatedEvent } }) => {
        const nextUpdatedEvent = filterEventValues(updatedEvent);

        let schedule = getSchedule(state, componentId);
        schedule = schedule.setIn(
            [listDays[updatedEvent.get('dayIndex')], updatedEvent.get('eventIndex')],
            nextUpdatedEvent
        );

        // $FlowFixMe
        return state.withMutations(s =>
            s
                .setIn(['components', componentId, 'schedule'], schedule)
                .setIn(['components', componentId, 'selectedEvent'], nextUpdatedEvent)
        );
    },

    [UPDATE_RECIPES]: updateRecipesSchedule,

    /*********************************
     *   ACTIONS SCHEDULES SECTION   *
     *********************************/

    [RECEIVE_ACTIONS]: handleReduce((state, { payload: actions }: ReceiveActions) =>
        // $FlowFixMe
        actions.reduce((newState, action) => {
            const scheduleEvent = action.events.find(event => event.type === 'schedule');
            return scheduleEvent
                ? // $FlowFixMe
                  newState.setIn(
                      ['data', action.id],
                      fromJS({
                          // $FlowFixMe
                          schedule: flowRight(prepareSchedule, fromJS)(scheduleEvent.settings.schedule),
                          scheduleType: scheduleType.RECIPE
                      })
                  )
                : newState;
        }, state)
    ),

    [SETUP_ACTION_FORM_SCHEDULE]: handleReduce((state, { payload: { defaultSchedule, schedule } }) => {
        // $FlowFixMe
        return state.deleteIn(['components', actionFormComponentId]).setIn(
            ['data', actionFormDataId],
            fromJS({
                schedule: schedule || prepareSchedule(defaultSchedule),
                scheduleType: scheduleType.RECIPE
            })
        );
    }),

    [EDIT_ACTION]: updateAction,

    [END_ATTEMPT_CREATE_ACTION]: updateAction
};

export default handleActions(actionHandlers, initialState);



// WEBPACK FOOTER //
// ./src/core/state/ui/schedule/index.js